import { MAX_TRANSCRIPT_SEARCH_CHARS } from "@repo/app-types/agents/customerInteractionFilters";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@repo/ui/toggleButton/segmentedControl";
import { IconSearch, IconSparkles } from "@tabler/icons-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";

const SEARCH_DEBOUNCE_MS = 500;

/**
 * Location-state key marking a navigation that (re)applies the `?q=` search.
 * Navigating to an identical URL doesn't change the search params, so callers
 * attach a fresh signal value to force the query to be applied again (e.g.
 * re-running "Search all calls for ..." after the input diverged).
 */
const transcriptSearchApplySignalKey = "transcriptSearchApplySignal";

/** Location state for navigations that should (re)apply the `?q=` search. */
export function buildTranscriptSearchApplyState(): Record<string, string> {
  return { [transcriptSearchApplySignalKey]: crypto.randomUUID() };
}

/** Segmented-control options, ordered standard (off) → deep (on). */
const searchDepthOptions: readonly SegmentedControlOption<
  "standard" | "deep"
>[] = [
  { id: "standard", label: "Standard" },
  {
    id: "deep",
    label: "Deep",
    icon: <IconSparkles className="size-3.5 shrink-0" />,
  },
];

/**
 * When a standard search returns fewer than this many results, we surface
 * the deep-search suggestion. Because input deep search suggestion shows up
 * at words >= 2, this is mainly a guard for single word queries (for now).
 */
const LOW_RESULT_SUGGESTION_THRESHOLD = 10;

/**
 * Transcript keyword search bar for the calls-list pages. Debounces typing by
 * 500ms and returns the trimmed value to thread into the recent-gradings query
 * args, where a change in `search` naturally resets the infinite-query
 * pagination (same mechanism as a `filters` change).
 */
export function useTranscriptSearch(): {
  search: string | undefined;
  deepSearchEnabled: boolean;
  renderSearchInput: (options: {
    resultCount: number | undefined;
    isLoadingResults: boolean;
  }) => ReactNode;
} {
  // Deep links (e.g. the search modal's "Search all calls for ...") pass the
  // initial query via `?q=`. The URL is authoritative: the query is applied
  // immediately (no debounce) on mount, whenever the param changes — including
  // its removal, which clears the search — and whenever a navigation carries a
  // fresh apply signal (see `buildTranscriptSearchApplyState`), which re-asserts
  // the param even when its value didn't change. Typing afterwards only updates
  // local state; the param is left as-is until the next such navigation.
  const [searchParams] = useSearchParams();
  const searchParamQuery = searchParams.get("q");
  const location = useLocation();
  const applySignal = (location.state as Record<string, unknown> | null)?.[
    transcriptSearchApplySignalKey
  ];

  const [searchQuery, setSearchQuery] = useState(searchParamQuery ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(
    searchParamQuery ?? "",
  );
  const [deepSearchEnabled, setDeepSearchEnabled] = useState(false);
  const [isDeepSearchToggleVisible, setIsDeepSearchToggleVisible] =
    useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastAppliedRef = useRef({ query: searchParamQuery, applySignal });
  useEffect(() => {
    const lastApplied = lastAppliedRef.current;
    if (
      searchParamQuery === lastApplied.query &&
      applySignal === lastApplied.applySignal
    ) {
      return;
    }
    lastAppliedRef.current = { query: searchParamQuery, applySignal };
    setSearchQuery(searchParamQuery ?? "");
    setDebouncedSearch(searchParamQuery ?? "");
    if (!searchParamQuery) {
      setDeepSearchEnabled(false);
      setIsDeepSearchToggleVisible(false);
    }
  }, [searchParamQuery, applySignal]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const trimmed = debouncedSearch.trim();
  const search = trimmed ? trimmed : undefined;
  const liveQuery = searchQuery.trim();

  const handleSearchQueryChange = (nextSearchQuery: string) => {
    setSearchQuery(nextSearchQuery);
    if (!nextSearchQuery.trim()) {
      setDeepSearchEnabled(false);
      setIsDeepSearchToggleVisible(false);
    }
  };

  const handleSearchClear = () => {
    handleSearchQueryChange("");
  };

  const handleEnableDeepSearch = () => {
    setDeepSearchEnabled(true);
    setIsDeepSearchToggleVisible(true);
  };

  const renderSearchInput = ({
    resultCount,
    isLoadingResults,
  }: {
    resultCount: number | undefined;
    isLoadingResults: boolean;
  }) => {
    const canSuggestDeepSearch =
      !deepSearchEnabled && !isDeepSearchToggleVisible;
    // Reactive trigger: the current exact-match search settled with few results.
    const shouldSuggestFromLowResults =
      canSuggestDeepSearch &&
      Boolean(search) &&
      !isLoadingResults &&
      resultCount !== undefined &&
      resultCount < LOW_RESULT_SUGGESTION_THRESHOLD;
    // Proactive trigger: the query looks conceptual before results come back.
    const shouldSuggestFromSemanticQuery =
      canSuggestDeepSearch && maybeSemanticQuery(liveQuery);
    const shouldSuggestDeepSearch =
      shouldSuggestFromLowResults || shouldSuggestFromSemanticQuery;
    const shouldShowDeepSearchHelper = deepSearchEnabled && liveQuery;
    const hasSearchHelper =
      shouldSuggestDeepSearch || Boolean(shouldShowDeepSearchHelper);
    const deepSearchSuggestionPrompt = shouldSuggestFromSemanticQuery
      ? "Searching by meaning, not keywords?"
      : resultCount === 0
        ? "No matches found."
        : "Only a few exact matches.";

    return (
      <div
        className={twMerge(
          "relative flex shrink-0 flex-col",
          hasSearchHelper && "pb-6",
        )}
      >
        <div className="flex items-center gap-1">
          <div className="w-64 shrink-0">
            <Input
              placeholder="Search transcripts..."
              value={searchQuery}
              onChange={(e) => handleSearchQueryChange(e.target.value)}
              onClear={handleSearchClear}
              leftIcon={<IconSearch className="h-4 w-4 text-gray-400" />}
              maxLength={MAX_TRANSCRIPT_SEARCH_CHARS}
              size={Size.SMALL}
            />
          </div>

          {isDeepSearchToggleVisible ? (
            <SegmentedControl
              aria-label="Search depth"
              size={Size.SMALL}
              className="shrink-0"
              options={searchDepthOptions}
              value={deepSearchEnabled ? "deep" : "standard"}
              onChange={(mode) => setDeepSearchEnabled(mode === "deep")}
            />
          ) : null}
        </div>

        {shouldSuggestDeepSearch ? (
          <div className="absolute top-9 left-0 flex w-max items-center gap-1.5 text-xs text-muted-foreground">
            <IconSparkles className="size-3.5 shrink-0 text-indigo-500" />
            <span>{deepSearchSuggestionPrompt}</span>
            <button
              type="button"
              className="font-medium text-indigo-700 underline-offset-2 hover:underline"
              onClick={handleEnableDeepSearch}
            >
              Try deep search
            </button>
          </div>
        ) : shouldShowDeepSearchHelper ? (
          <div className="absolute top-9 left-0 flex w-max items-center gap-1.5 text-xs text-muted-foreground">
            <IconSparkles className="size-3.5 shrink-0 text-indigo-500" />
            <span>
              Finds related moments even when the transcript does not use these
              exact words. May take longer.
            </span>
          </div>
        ) : null}
      </div>
    );
  };

  return { search, deepSearchEnabled, renderSearchInput };
}

/**
 * Gate showing deep search by 'guessing' user intent:
 * - At least 2 words
 * - No quotes (intending exact match)
 */
function maybeSemanticQuery(query: string): boolean {
  const tokens = query.split(/\s+/).filter(Boolean);
  return tokens.length >= 2 && !query.includes('"');
}
